import React from "react";
import { Link } from "react-router-dom";
import solanaMark from "../../assets/landing/solana/solana-mark.svg";
import checkIcon from "../../assets/landing/solana/check-icon.svg";
import arrowRight from "../../assets/landing/solana/arrow-right.svg";
import nodefleetLogo from "../../assets/landing/solana/nodefleet-logo.svg";
import socialsIcons from "../../assets/landing/solana/socials.svg";

// Card content for the two Solana gRPC tiers. The Starter limits below
// MIRROR what the gateway actually enforces — see datanodes-deployment
// docs/solana-grpc-launch.md and ratelimit:limits:org:<uuid> grpc_streams.
// When `Plan.grpc_streams` ships in the backend (follow-up PR), this
// component can pull pricing + limits from plans.json instead of the
// hard-coded copy here.
const STARTER_PLAN = {
  label: "Solana gRPC Starter",
  intro: "Production Solana data streaming, one monthly subscription.",
  description:
    "Authenticated access to our shared yellowstone-grpc geyser pool on bare-metal EU + US Agave validators. Set x-api-key on your gRPC metadata and start subscribing.",
  bullets: [
    "1 concurrent gRPC stream",
    "Up to 5 accounts or owners per filter",
    "Real-time subscriptions: accounts, transactions, slots, block metadata",
    "EU primary with US automatic failover",
    "Email support (24h response)",
  ],
  ctaLabel: "$199 / month",
  // Routes to the /solana-grpc dashboard page (ProtectedRoute forwards to
  // /login if not authed, then bounces back after signin). That page
  // handles the Stripe Checkout flow + connection guide.
  ctaHref: "/#/solana-grpc",
};

const DEDICATED_PLAN = {
  label: "Dedicated Cluster",
  intro:
    "Your own yellowstone-grpc stack. Built for MEV bots, arb scanners, and wallet providers.",
  description:
    "Isolated geyser instance on bare-metal validators, with custom filter limits and zero co-tenant churn. Choose your region, your subscription mix, your reject lists.",
  bullets: [
    "Unlimited concurrent streams",
    "Unlimited filter complexity",
    "Full subscription mix (incl. blocks-with-transactions + entries)",
    "Region choice (EU, US, or both)",
    "Dedicated Slack channel, 4h response SLA",
    "Optional Kafka forwarding (yellowstone-grpc-kafka)",
  ],
  ctaLabel: "Contact us",
  ctaHref:
    "mailto:nodes@nodefleet.net?subject=Dedicated%20Solana%20gRPC%20cluster",
};

// Single shared mailto target reused by the hero + CTA strip "Contact Sales"
// buttons. Keep in sync with DEDICATED_PLAN.ctaHref above.
const CONTACT_SALES_HREF =
  "mailto:nodes@nodefleet.net?subject=Solana%20gRPC%20inquiry";
const GET_STARTED_HREF = "/#/solana-grpc";

// Both hero/CTA-strip buttons render as <a> so a single href prop drives
// internal navigation (HashRouter), external links, and mailto: targets.
// Targets with the `mailto:` scheme are left without target=_blank so the
// user's default mail client opens in the foreground.
const PrimaryButton = ({ children, href }) => (
  <a
    href={href}
    className="inline-flex items-center justify-center gap-2 px-8 py-[17px] font-secondary font-bold text-[18px] leading-7 text-[#00324B] transition-opacity hover:opacity-90"
    style={{
      backgroundImage:
        "linear-gradient(133.82deg, #49B7F8 0%, #36AAEA 100%)",
    }}
  >
    <span>{children}</span>
    <img src={arrowRight} alt="" aria-hidden="true" className="h-4 w-4" />
  </a>
);

const SecondaryButton = ({ children, href }) => (
  <a
    href={href}
    className="inline-flex items-center justify-center px-[33px] py-[17px] font-secondary font-bold text-[18px] leading-7 text-[#FDFBFE] border border-solid border-[rgba(71,72,74,0.3)] bg-[rgba(36,38,41,0.4)] backdrop-blur-[10px] transition-colors hover:bg-[rgba(36,38,41,0.6)]"
  >
    {children}
  </a>
);

const CheckBullet = ({ children }) => (
  <li className="flex items-start gap-[6px]">
    <img
      src={checkIcon}
      alt=""
      aria-hidden="true"
      className="mt-[1px] h-[26px] w-[26px] shrink-0"
    />
    <span className="font-secondary text-[14px] leading-[26px] text-white">
      {children}
    </span>
  </li>
);

const PricingCard = ({ plan }) => (
  <article className="relative flex w-[463px] flex-col bg-[#2A2A2A] border-t-4 border-solid border-[#02A7E8] overflow-hidden">
    {/* Chip label */}
    <div className="mx-[38px] mt-[75px] flex h-[81px] w-[289px] items-center bg-[rgba(65,146,200,0.15)] border border-solid border-[rgba(71,72,74,0.2)] px-[13px] py-[5px]">
      <span className="font-grotesk text-[20px] uppercase tracking-[1.2px] leading-6 text-[#49B7F8]">
        {plan.label}
      </span>
    </div>

    <div className="mx-[38px] mt-[34px]">
      <p className="font-secondary font-bold text-[16px] leading-[26px] text-white opacity-80">
        {plan.intro}
      </p>
      <p className="mt-[14px] font-secondary text-[14px] leading-5 text-white opacity-80">
        {plan.description}
      </p>
    </div>

    <ul className="mx-[38px] mt-[24px] flex flex-col gap-[10px]">
      {plan.bullets.map((b) => (
        <CheckBullet key={b}>{b}</CheckBullet>
      ))}
    </ul>

    <a
      href={plan.ctaHref}
      className="mt-auto mb-[40px] mx-[42px] inline-flex items-center justify-center gap-2 pr-8 py-[17px] font-secondary font-bold text-[18px] leading-7 text-[#49B7F8] hover:underline"
    >
      <span>{plan.ctaLabel}</span>
      <img src={arrowRight} alt="" aria-hidden="true" className="h-4 w-4" />
    </a>
  </article>
);

const SolanaLanding = () => {
  return (
    <main className="bg-[#131313] text-white">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-16 pt-[148px] pb-[100px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 flex flex-col gap-6 self-center">
              <h1 className="font-jakarta font-bold text-[56px] lg:text-[72px] leading-[1] tracking-[-1.8px] text-[#FDFBFE]">
                Live <span className="text-[#02A7E8]">Solana Data,</span>{" "}
                Delivered at scale
              </h1>
              <p className="max-w-[576px] font-secondary text-[20px] leading-7 text-[#ABABAD]">
                Tap into low-latency Solana data streams powered by top
                providers and a globally resilient network.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <PrimaryButton href={GET_STARTED_HREF}>Get Started</PrimaryButton>
                <SecondaryButton href={CONTACT_SALES_HREF}>Contact Sales</SecondaryButton>
              </div>
            </div>
          </div>
        </div>
        {/* Ambient Solana mark — right side, low opacity */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-[72px] right-[-3%] hidden lg:block opacity-40"
        >
          <div className="relative h-[820px] w-[820px] mix-blend-screen">
            <div
              className="absolute inset-0 bg-gradient-to-l from-[rgba(136,206,255,0.1)] to-transparent"
              aria-hidden="true"
            />
            <img
              src={solanaMark}
              alt=""
              className="absolute left-[209px] top-[251px] h-[415px] w-[415px] opacity-60"
            />
          </div>
        </div>
      </section>

      {/* ===== WORKLOAD PRICING ===== */}
      <section className="bg-[#1B1B1B] pt-[50px] pb-[128px]">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-[30px] px-6">
          <div className="flex flex-col items-center text-center gap-6">
            <h2 className="font-jakarta font-bold text-[44px] lg:text-[60px] leading-[1] tracking-[-3px] text-[#E2E2E2] max-w-[930px]">
              Built for Every Solana <span className="text-[#49B7F8]">Workload</span>
            </h2>
            <p className="max-w-[852px] font-secondary text-[18px] leading-8 text-white">
              Run on shared Solana gRPC infrastructure for flexibility, or
              deploy dedicated clusters for mission-critical, low-latency
              applications.
            </p>
          </div>
          <div className="flex flex-col lg:flex-row gap-5 items-stretch justify-center w-full">
            <PricingCard plan={STARTER_PLAN} />
            <PricingCard plan={DEDICATED_PLAN} />
          </div>
        </div>

      </section>

      {/* ===== CTA STRIP ===== */}
      <section className="bg-[#131313]">
        <div className="mx-auto flex max-w-[1280px] flex-col lg:flex-row items-start lg:items-center justify-between gap-8 px-6 lg:px-16 py-[120px]">
          <h2 className="font-jakarta font-bold text-[28px] lg:text-[36px] leading-[40px] tracking-[-0.9px] text-white max-w-[683px]">
            Whether you&apos;re scaling an application or running latency-sensitive
            workloads, accessing Solana gRPC is simple.
          </h2>
          <div className="flex flex-wrap gap-4 shrink-0">
            <PrimaryButton>Get Started</PrimaryButton>
            <SecondaryButton>Contact Sales</SecondaryButton>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#131313] pt-[80px] pb-[40px]">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-6 px-6">
          <nav className="flex items-center gap-[26px]">
            <Link
              to="/"
              className="font-secondary text-[14px] leading-6 text-white hover:text-[#49B7F8] transition-colors"
            >
              About
            </Link>
            <Link
              to="/pricing"
              className="font-secondary text-[14px] leading-6 text-white hover:text-[#49B7F8] transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/docs"
              className="font-secondary text-[14px] leading-6 text-white hover:text-[#49B7F8] transition-colors"
            >
              Docs
            </Link>
          </nav>
          <img
            src={socialsIcons}
            alt="Social channels"
            className="h-6 w-[153px]"
          />
          <img
            src={nodefleetLogo}
            alt="Nodefleet"
            className="h-[40px] w-[127px] opacity-70"
          />
          <p className="font-secondary text-[12px] leading-5 text-[#6B7280] text-center">
            &copy; 2026 Nodefleet LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
};

export default SolanaLanding;
